import { useState } from "react";
import { Link, useParams } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  ArrowLeft,
  Check,
  Pencil,
  Flag,
  Sparkles,
  FileOutput,
  AlertTriangle,
  X,
} from "lucide-react";
import { TenantLayout, useTenant } from "@/components/tenant/tenant-layout";
import { useAuth } from "@/lib/auth/auth-provider";
import { useRole } from "@/lib/auth/use-role";
import {
  Badge,
  Button,
  Card,
  CardHeader,
  ConfidenceChip,
  Dot,
  PageHeader,
  Spinner,
} from "@/components/ui/primitives";
import {
  acceptField,
  draftFilingWithAI,
  fieldDefsForForm,
  getFiling,
  listFilingEvents,
  listFilingFields,
  saveFieldValue,
  setFilingStatus,
  type FilingFieldRow,
  type FilingStatus,
} from "@/lib/api/filings";
import { listFunds } from "@/lib/api/funds";
import {
  FILING_STATUS_LABEL,
  FILING_STATUS_TONE,
  FORM_LABEL,
  FUND_TYPE_LABEL,
  REVIEW_LABEL,
  REVIEW_TONE,
  SECTION_BLURB,
  SECTION_LABEL,
  SECTION_ORDER_BY_FORM,
  SOURCE_HINT,
  SOURCE_LABEL,
  SOURCE_TONE,
} from "@/lib/filing-labels";
import {
  filingCompleteness,
  isFilingReady,
  type FormPfFieldDef,
  type FormPfFundType,
} from "@argus/engine";

/* ----------------------------------------------------------- value display */

function fmtUsd(n: number): string {
  if (n >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  return `$${n.toLocaleString("en-US")}`;
}

interface CompositionEntry {
  type: string;
  amount: number;
  pct: number;
}

/** Safe stringify for an `unknown` jsonb value (never "[object Object]"). */
function asText(value: unknown): string {
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  return JSON.stringify(value);
}

function displayValue(def: FormPfFieldDef, value: unknown): string {
  if (value === null || value === undefined || value === "") return "—";
  switch (def.type) {
    case "currency":
      return fmtUsd(Number(value));
    case "percent":
      return `${Number(value)}%`;
    case "boolean":
      return value ? "Yes" : "No";
    case "enum":
      // includes (not startsWith) so Form ADV's `adv_fund_type` fields render
      // with the same fund-type labels as Form PF's `fund_type`.
      return def.key.includes("fund_type")
        ? FUND_TYPE_LABEL[value as FormPfFundType]
        : asText(value);
    case "composition":
      return (value as CompositionEntry[]).map((c) => `${c.type} ${c.pct}%`).join(" · ");
    default:
      return asText(value);
  }
}

/* ----------------------------------------------------------- field editing */

function FieldRow({
  field,
  def,
  canEdit,
  onAccept,
  onSave,
  onFlag,
  busy,
}: {
  field: FilingFieldRow;
  def: FormPfFieldDef;
  canEdit: boolean;
  onAccept: () => void;
  onSave: (value: unknown) => void;
  onFlag: () => void;
  busy: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<string>(
    field.value === null || field.value === undefined ? "" : asText(field.value),
  );

  const hasValue = field.value !== null && field.value !== undefined && field.value !== "";
  const composition = def.type === "composition";

  function parse(): unknown {
    const raw = draft.trim();
    if (raw === "") return null;
    if (def.type === "integer" || def.type === "currency" || def.type === "percent") {
      const n = Number(raw.replace(/[$,%\s]/g, ""));
      return Number.isFinite(n) ? n : raw;
    }
    if (def.type === "boolean") return raw.toLowerCase().startsWith("y") || raw === "true";
    return raw;
  }

  return (
    <div className="px-4 py-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-sm font-medium text-neutral-200">{def.label}</span>
            <span className="text-[10px] uppercase tracking-wide text-neutral-600">{def.item}</span>
          </div>
          {def.help && <p className="mt-0.5 text-[11px] text-neutral-500">{def.help}</p>}
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          <Badge tone={SOURCE_TONE[field.source]} className="capitalize">
            {SOURCE_LABEL[field.source]}
          </Badge>
          {field.is_gap && !hasValue && (
            <Badge tone="risk">
              <Dot tone="risk" /> Gap
            </Badge>
          )}
          {field.confidence != null && hasValue && <ConfidenceChip value={field.confidence} />}
          {hasValue && (
            <Badge tone={REVIEW_TONE[field.review_status]}>
              {REVIEW_LABEL[field.review_status]}
            </Badge>
          )}
        </div>
      </div>

      <div className="mt-2 flex items-start justify-between gap-3">
        {editing ? (
          <div className="flex flex-1 items-center gap-2">
            {def.type === "enum" && def.options ? (
              <select
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                className="flex-1 rounded-md border border-neutral-700 bg-neutral-950 px-2.5 py-1.5 text-sm text-neutral-100"
              >
                <option value="">— select —</option>
                {def.options.map((o) => (
                  <option key={o} value={o}>
                    {def.key.includes("fund_type") ? FUND_TYPE_LABEL[o as FormPfFundType] : o}
                  </option>
                ))}
              </select>
            ) : def.type === "boolean" ? (
              <select
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                className="flex-1 rounded-md border border-neutral-700 bg-neutral-950 px-2.5 py-1.5 text-sm text-neutral-100"
              >
                <option value="">— select —</option>
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
            ) : def.type === "longtext" ? (
              <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                rows={4}
                className="flex-1 rounded-md border border-neutral-700 bg-neutral-950 px-2.5 py-1.5 text-sm text-neutral-100"
              />
            ) : (
              <input
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder={def.type === "currency" ? "e.g. 850000000" : ""}
                className="flex-1 rounded-md border border-neutral-700 bg-neutral-950 px-2.5 py-1.5 text-sm text-neutral-100"
              />
            )}
            <Button
              size="sm"
              variant="success"
              disabled={busy}
              onClick={() => {
                onSave(parse());
                setEditing(false);
              }}
            >
              <Check className="size-3.5" /> Save
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>
              <X className="size-3.5" />
            </Button>
          </div>
        ) : (
          <div
            className={`flex-1 text-sm ${hasValue ? "text-neutral-100" : "italic text-neutral-600"}`}
          >
            {displayValue(def, field.value)}
            {field.citation && (
              <span className="ml-2 text-[11px] text-neutral-500">· {field.citation}</span>
            )}
          </div>
        )}

        {canEdit && !editing && (
          <div className="flex shrink-0 items-center gap-1">
            {hasValue && field.review_status === "unreviewed" && field.source !== "manual" && (
              <Button
                size="sm"
                variant="ghost"
                disabled={busy}
                onClick={onAccept}
                title="Accept as-is"
              >
                <Check className="size-3.5" /> Accept
              </Button>
            )}
            {!composition && (
              <Button
                size="sm"
                variant="ghost"
                disabled={busy}
                onClick={() => setEditing(true)}
                title={hasValue ? "Edit" : "Enter value"}
              >
                <Pencil className="size-3.5" /> {hasValue ? "Edit" : "Enter"}
              </Button>
            )}
            {hasValue && field.review_status !== "flagged" && (
              <Button
                size="sm"
                variant="ghost"
                disabled={busy}
                onClick={onFlag}
                title="Flag for follow-up"
              >
                <Flag className="size-3.5" />
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------- workspace */

function WorkspaceInner({ filingId }: { filingId: string }) {
  const tenant = useTenant();
  const { user } = useAuth();
  const qc = useQueryClient();
  const { canWrite, role } = useRole(tenant.id);
  const canEdit = canWrite || role === "reviewer";

  const {
    data: filing,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["filing", filingId],
    queryFn: () => getFiling(filingId),
  });
  const { data: fields } = useQuery({
    queryKey: ["filing-fields", filingId],
    queryFn: () => listFilingFields(filingId),
  });
  const { data: events } = useQuery({
    queryKey: ["filing-events", filingId],
    queryFn: () => listFilingEvents(filingId),
  });
  const { data: funds } = useQuery({
    queryKey: ["funds", tenant.id],
    queryFn: () => listFunds(tenant.id),
  });

  function invalidate() {
    void qc.invalidateQueries({ queryKey: ["filing", filingId] });
    void qc.invalidateQueries({ queryKey: ["filing-fields", filingId] });
    void qc.invalidateQueries({ queryKey: ["filing-events", filingId] });
  }

  const draft = useMutation({
    mutationFn: () => {
      if (!user || !filing) throw new Error("Not ready.");
      return draftFilingWithAI(filing, user.id);
    },
    onSuccess: (r) => {
      invalidate();
      toast.success(
        `${r.configured ? "Claude" : "Deterministic"} draft applied to ${r.applied} field(s).`,
        {
          description: r.configured
            ? r.engine
            : "Set ANTHROPIC_API_KEY to enrich with the live model.",
        },
      );
    },
    onError: (e: Error) => toast.error("Drafting failed", { description: e.message }),
  });

  const saveField = useMutation({
    mutationFn: ({ f, value }: { f: FilingFieldRow; value: unknown }) => {
      if (!user) throw new Error("Not signed in.");
      return saveFieldValue(f, { value }, user.id);
    },
    onSuccess: invalidate,
  });
  const accept = useMutation({
    mutationFn: (f: FilingFieldRow) => {
      if (!user) throw new Error("Not signed in.");
      return acceptField(f, user.id);
    },
    onSuccess: invalidate,
  });
  const flag = useMutation({
    mutationFn: (f: FilingFieldRow) => {
      if (!user) throw new Error("Not signed in.");
      return saveFieldValue(f, { reviewStatus: "flagged" }, user.id);
    },
    onSuccess: invalidate,
  });
  const status = useMutation({
    mutationFn: ({ to }: { to: FilingStatus }) => {
      if (!user || !filing) throw new Error("Not ready.");
      return setFilingStatus(filing, to, user.id);
    },
    onSuccess: () => {
      invalidate();
      toast.success("Filing status updated.");
    },
  });

  if (isLoading) return <p className="text-sm text-neutral-500">Loading filing…</p>;
  if (error || !filing)
    return (
      <div className="rounded border border-red-900/60 bg-red-950/30 px-3 py-2 text-xs text-red-300">
        {error?.message ?? "Filing not found."}
      </div>
    );

  const defs = fieldDefsForForm(filing.form_type, funds ?? []);
  const sectionOrder = SECTION_ORDER_BY_FORM[filing.form_type];
  const fundName = new Map((funds ?? []).map((f) => [f.id, f.short_name ?? f.name]));
  const rows = fields ?? [];

  const completeness = filingCompleteness(
    rows.map((f) => ({
      section: f.section,
      source: f.source,
      value: f.value,
      isGap: f.is_gap,
      reviewStatus: f.review_status,
    })),
  );
  const ready = isFilingReady(
    rows.map((f) => ({
      section: f.section,
      source: f.source,
      value: f.value,
      isGap: f.is_gap,
      reviewStatus: f.review_status,
    })),
  );
  const busy = saveField.isPending || accept.isPending || flag.isPending;

  return (
    <>
      <Link
        to="/t/$slug/filings"
        params={{ slug: tenant.slug }}
        className="inline-flex items-center gap-1.5 text-xs text-neutral-500 hover:text-neutral-300"
      >
        <ArrowLeft className="size-3.5" /> Filings
      </Link>

      <PageHeader
        title={filing.title}
        subtitle={
          <span className="flex flex-wrap items-center gap-1.5">
            <Badge tone="neutral">{FORM_LABEL[filing.form_type]}</Badge>
            {filing.due_date && <span className="text-neutral-500">Due {filing.due_date}</span>}
            {filing.engine_version && (
              <span className="text-neutral-600">· model {filing.engine_version}</span>
            )}
          </span>
        }
        actions={
          <div className="flex items-center gap-2">
            {canEdit && (
              <Button variant="secondary" disabled={draft.isPending} onClick={() => draft.mutate()}>
                {draft.isPending ? <Spinner className="size-4" /> : <Sparkles className="size-4" />}
                Draft with AI
              </Button>
            )}
            <Link to="/t/$slug/filings/$filingId/package" params={{ slug: tenant.slug, filingId }}>
              <Button variant="primary">
                <FileOutput className="size-4" /> Filing-ready package
              </Button>
            </Link>
            <Badge tone={FILING_STATUS_TONE[filing.status]}>
              {FILING_STATUS_LABEL[filing.status]}
            </Badge>
          </div>
        }
      />

      {/* Completeness summary */}
      <Card className="p-4">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-6 text-sm">
            <Stat label="Populated" value={`${completeness.populated}/${completeness.total}`} />
            <Stat label="Reviewed" value={`${completeness.reviewedPct}%`} />
            <Stat
              label="Gaps"
              value={completeness.gaps}
              tone={completeness.gaps > 0 ? "risk" : "clear"}
            />
            <Stat
              label="AI pending"
              value={completeness.aiPending}
              tone={completeness.aiPending > 0 ? "ai" : "clear"}
            />
          </div>
          <div className="flex items-center gap-2">
            <div className="h-1.5 w-40 overflow-hidden rounded-full bg-neutral-800">
              <div
                className="bg-brand-500 h-full rounded-full transition-all"
                style={{ width: `${completeness.pct}%` }}
              />
            </div>
            <span className="text-[11px] tabular-nums text-neutral-500">{completeness.pct}%</span>
          </div>
        </div>
        {canEdit && (
          <div className="mt-3 flex flex-wrap items-center gap-1.5 border-t border-neutral-800/70 pt-3">
            <span className="mr-1 text-[11px] uppercase tracking-wide text-neutral-600">
              Status
            </span>
            {(["draft", "in_review", "ready", "filed", "withdrawn"] as FilingStatus[])
              .filter((s) => s !== filing.status)
              .map((s) => (
                <Button
                  key={s}
                  size="sm"
                  variant="ghost"
                  disabled={status.isPending || (s === "ready" && !ready)}
                  title={
                    s === "ready" && !ready
                      ? "Every field must be populated and reviewed first"
                      : ""
                  }
                  onClick={() => status.mutate({ to: s })}
                >
                  {FILING_STATUS_LABEL[s]}
                </Button>
              ))}
            {!ready && completeness.gaps > 0 && (
              <span className="ml-1 inline-flex items-center gap-1 text-[11px] text-amber-300/80">
                <AlertTriangle className="size-3" /> {completeness.gaps} gap(s) to resolve before
                filing-ready
              </span>
            )}
          </div>
        )}
      </Card>

      <div className="grid gap-4 lg:grid-cols-[1fr_300px]">
        <div className="space-y-4">
          {sectionOrder.map((section) => {
            const sectionRows = rows.filter((f) => f.section === section);
            if (sectionRows.length === 0) return null;
            const prog = completeness.bySection[section];
            // Per-fund sections have fund-suffixed field keys (`key__<fundId>`).
            const isPerFund = sectionRows.some((f) => f.field_key.includes("__"));
            return (
              <Card key={section}>
                <CardHeader
                  title={SECTION_LABEL[section]}
                  action={
                    prog ? (
                      <span className="text-[11px] tabular-nums text-neutral-500">
                        {prog.populated}/{prog.total}
                        {prog.gaps > 0 ? ` · ${prog.gaps} gap` : ""}
                      </span>
                    ) : undefined
                  }
                />
                <p className="px-4 pb-1 text-[11px] text-neutral-500">{SECTION_BLURB[section]}</p>
                {isPerFund ? (
                  groupByFund(sectionRows).map(([fundId, frows]) => (
                    <div key={fundId} className="border-t border-neutral-800/70">
                      <div className="bg-neutral-900/40 px-4 py-1.5 text-[11px] font-medium uppercase tracking-wide text-neutral-400">
                        {fundName.get(fundId) ?? "Fund"}
                      </div>
                      <div className="divide-y divide-neutral-800/60">
                        {frows.map((f) => {
                          const def = defs.get(f.field_key);
                          return def ? (
                            <FieldRow
                              key={f.id}
                              field={f}
                              def={def}
                              canEdit={canEdit}
                              busy={busy}
                              onAccept={() => accept.mutate(f)}
                              onSave={(v) => saveField.mutate({ f, value: v })}
                              onFlag={() => flag.mutate(f)}
                            />
                          ) : null;
                        })}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="divide-y divide-neutral-800/60 border-t border-neutral-800/70">
                    {sectionRows.map((f) => {
                      const def = defs.get(f.field_key);
                      return def ? (
                        <FieldRow
                          key={f.id}
                          field={f}
                          def={def}
                          canEdit={canEdit}
                          busy={busy}
                          onAccept={() => accept.mutate(f)}
                          onSave={(v) => saveField.mutate({ f, value: v })}
                          onFlag={() => flag.mutate(f)}
                        />
                      ) : null;
                    })}
                  </div>
                )}
              </Card>
            );
          })}
        </div>

        {/* Activity + provenance legend */}
        <div className="space-y-4">
          <Card>
            <CardHeader title="Provenance" />
            <div className="space-y-2 px-4 pb-4 text-[11px]">
              {(["auto", "ai", "manual"] as const).map((s) => (
                <div key={s} className="flex items-start gap-2">
                  <Badge tone={SOURCE_TONE[s]}>{SOURCE_LABEL[s]}</Badge>
                  <span className="text-neutral-500">{SOURCE_HINT[s]}</span>
                </div>
              ))}
            </div>
          </Card>

          <Card>
            <CardHeader title="Activity" />
            <div className="max-h-96 space-y-2.5 overflow-y-auto px-4 pb-4 text-xs">
              {(events ?? []).length === 0 ? (
                <p className="text-neutral-500">No activity yet.</p>
              ) : (
                (events ?? []).map((ev) => (
                  <div key={ev.id} className="flex gap-2">
                    <Dot tone="neutral" className="mt-1.5" />
                    <div className="min-w-0">
                      <div className="text-neutral-300">
                        {ev.event_type === "status_change" || ev.event_type === "reopened"
                          ? `${ev.from_status ?? "?"} → ${ev.to_status ?? "?"}`
                          : ev.event_type.replace(/_/g, " ")}
                        {ev.field_key ? (
                          <span className="text-neutral-600"> · {ev.field_key}</span>
                        ) : null}
                      </div>
                      {ev.note && <div className="text-neutral-500">{ev.note}</div>}
                      <div className="text-neutral-600">
                        {new Date(ev.created_at).toLocaleString()}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </Card>
        </div>
      </div>
    </>
  );
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: string | number;
  tone?: "risk" | "clear" | "ai";
}) {
  const color =
    tone === "risk"
      ? "text-amber-300"
      : tone === "ai"
        ? "text-indigo-300"
        : tone === "clear"
          ? "text-emerald-300"
          : "text-neutral-100";
  return (
    <div>
      <div className={`text-base font-semibold tabular-nums ${color}`}>{value}</div>
      <div className="text-[10px] uppercase tracking-wide text-neutral-600">{label}</div>
    </div>
  );
}

function groupByFund(rows: FilingFieldRow[]): [string, FilingFieldRow[]][] {
  const groups: [string, FilingFieldRow[]][] = [];
  const index = new Map<string, FilingFieldRow[]>();
  for (const r of rows) {
    const fundId = r.field_key.split("__")[1] ?? "—";
    let arr = index.get(fundId);
    if (!arr) {
      arr = [];
      index.set(fundId, arr);
      groups.push([fundId, arr]);
    }
    arr.push(r);
  }
  return groups;
}

export function FilingWorkspacePage() {
  const { filingId } = useParams({ strict: false });
  return (
    <TenantLayout>
      <WorkspaceInner filingId={filingId ?? ""} />
    </TenantLayout>
  );
}
